import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Button,
  Chip,
  CircularProgress,
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material'
import {
  AccessTime as AccessIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  ContentCopy,
  OpenInNew,
} from '@mui/icons-material'
import moment from 'moment'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { Pagination } from 'components'
import { get } from 'services/api'
import { toast } from 'react-toastify'
import { formatDuration } from 'utils/functions'
import UserActivityLogDetails from '../../components/UserActivityLogDetails'
import styles from './styles'

const USER_ACTIVITY_FILTER_PARAMS = [
  'user_names_or_emails',
  'user_names',
  'user_emails',
  'methods',
  'ip_addresses',
  'actions',
  'status_codes',
  'was_successful',
  'user_session_ids',
  'not_user_session_ids',
  'start_date',
  'end_date',
]

const getFiltersFromQueryParams = (search) => {
  const params = new URLSearchParams(search)
  const filters = []
  const userFilters = new Map()

  params.getAll('user_names_or_emails').forEach((value) => {
    if (!userFilters.has(value)) {
      const filter = { type: 'user', value }
      userFilters.set(value, filter)
      filters.push(filter)
    }
  })

  const repeatedParams = [
    ['ip_addresses', 'ip'],
    ['methods', 'method'],
    ['actions', 'actions'],
    ['status_codes', 'httpStatus'],
    ['user_session_ids', 'sessionId', 'contains'],
    ['not_user_session_ids', 'sessionId', 'notContains'],
  ]
  repeatedParams.forEach(([parameter, type, operator]) => {
    params.getAll(parameter).forEach((value) => {
      filters.push({
        type,
        value,
        ...(type === 'actions' ? { label: value } : {}),
        ...(operator ? { operator } : {}),
      })
    })
  })

  params.getAll('was_successful').forEach((value) => {
    filters.push({ type: 'status', value: value === 'true' ? 'success' : 'error' })
  })

  const startDate = params.get('start_date')
  const endDate = params.get('end_date')
  if (startDate || endDate) filters.push({ type: 'dateRange', startDate, endDate })

  return filters
}

const appendFilterQueryParams = (params, filters) => {
  filters.forEach((filter) => {
    if (filter.type === 'user') {
      params.append('user_names_or_emails', filter.value)
    } else if (filter.type === 'ip') {
      params.append('ip_addresses', filter.value)
    } else if (filter.type === 'method') {
      params.append('methods', filter.value)
    } else if (filter.type === 'status') {
      params.append('was_successful', String(filter.value === 'success'))
    } else if (filter.type === 'actions') {
      params.append('actions', filter.value)
    } else if (filter.type === 'sessionId') {
      params.append(filter.operator === 'notContains' ? 'not_user_session_ids' : 'user_session_ids', filter.value)
    } else if (filter.type === 'dateRange') {
      if (filter.startDate) params.append('start_date', filter.startDate)
      if (filter.endDate) params.append('end_date', filter.endDate)
    } else if (filter.type === 'httpStatus') {
      params.append('status_codes', filter.value)
    }
  })
}

const UserActivityLogMonitor = () => {
  const classes = styles()
  const history = useHistory()
  const location = useLocation()
  const token = useSelector((state) => state.auth.access_token)
  const scopeOptions = useSelector((state) => state.auth.scopeOptions || [])
  const currentSessionId = useSelector((state) => state.auth.user_session_id)
  const initialQueryParams = new URLSearchParams(location.search)
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(() => Math.max(0, (parseInt(initialQueryParams.get('page'), 10) || 1) - 1))
  const [size, setSize] = useState(() => parseInt(initialQueryParams.get('size'), 10) || 10)
  const [total, setTotal] = useState(0)
  const [filterType, setFilterType] = useState('user')
  const [searchValue, setSearchValue] = useState('')
  const [startDate, setStartDate] = useState(() => (
    initialQueryParams.get('start_date') ? moment(initialQueryParams.get('start_date')) : null
  ))
  const [endDate, setEndDate] = useState(() => (
    initialQueryParams.get('end_date') ? moment(initialQueryParams.get('end_date')) : null
  ))
  const [sessionIdOperator, setSessionIdOperator] = useState('contains')
  const [selectedActions, setSelectedActions] = useState([])
  const [filters, setFilters] = useState(() => getFiltersFromQueryParams(location.search))
  const [selectedLogUuid, setSelectedLogUuid] = useState(null)
  
  useEffect(() => {
    if (!currentSessionId) return
    setFilters((currentFilters) => {
      const hasSessionFilter = currentFilters.some((filter) => (
        filter.type === 'sessionId' && filter.value === currentSessionId
      ))
      return hasSessionFilter
        ? currentFilters
        : [...currentFilters, { type: 'sessionId', operator: 'notContains', value: currentSessionId }]
    })
  }, [currentSessionId])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    USER_ACTIVITY_FILTER_PARAMS.forEach((parameter) => params.delete(parameter))
    params.set('page', String(page + 1))
    params.set('size', String(size))
    appendFilterQueryParams(params, filters)

    const query = params.toString()
    const search = query ? `?${query}` : ''
    if (search !== location.search) {
      history.replace({ pathname: location.pathname, search, hash: location.hash })
    }
  }, [filters, history, location.hash, location.pathname, location.search, page, size])

  useEffect(() => {
    const getData = async () => {
      setLoading(true)
      const params = new URLSearchParams({ page: String(page + 1), size: String(size) })
      appendFilterQueryParams(params, filters)

      const response = await get({
        url: '/user-activity',
        token,
        params,
      })
      setLogs(response?.items || [])
      setTotal(response?.total || 0)
      setLoading(false)
    }

    getData()
  }, [page, size, token, filters])

  const handleApplyFilter = (event) => {
    event.preventDefault()
    if (filterType === 'actions') {
      if (!selectedActions.length) return
      setFilters((currentFilters) => {
        const nextFilters = [...currentFilters]
        selectedActions.forEach((action) => {
          if (!nextFilters.some((filter) => filter.type === 'actions' && filter.value === action.name)) {
            nextFilters.push({ type: 'actions', value: action.name, label: action.description })
          }
        })
        return nextFilters
      })
      setSelectedActions([])
      setPage(0)
      return
    }
    if (filterType === 'dateRange') {
      if (!startDate && !endDate) return
      if ((startDate && !startDate.isValid()) || (endDate && !endDate.isValid())) return
      if (startDate && endDate && endDate.isBefore(startDate, 'day')) return

      const dateFilter = {
        type: 'dateRange',
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      }
      setFilters((currentFilters) => [
        ...currentFilters.filter((filter) => filter.type !== 'dateRange'),
        dateFilter,
      ])
      setStartDate(null)
      setEndDate(null)
      setPage(0)
      return
    }

    const value = searchValue.trim()
    if (!value) return
    if (filterType === 'httpStatus' && (!Number.isInteger(Number(value)) || Number(value) < 100 || Number(value) > 599)) return

    setFilters((currentFilters) => {
      const filter = filterType === 'sessionId'
        ? { type: filterType, value, operator: sessionIdOperator }
        : { type: filterType, value }
      const alreadyApplied = currentFilters.some((currentFilter) => (
        currentFilter.type === filter.type
        && currentFilter.value === filter.value
        && (filter.type !== 'sessionId' || currentFilter.operator === filter.operator)
      ))
      return alreadyApplied ? currentFilters : [...currentFilters, filter]
    })
    setPage(0)
    setSearchValue('')
  }

  const handleRemoveFilter = (filterIndex) => {
    setFilters((currentFilters) => currentFilters.filter((filter, index) => index !== filterIndex))
    setPage(0)
  }

  const handleClearFilters = () => {
    setFilters([])
    setSearchValue('')
    setStartDate(null)
    setEndDate(null)
    setSelectedActions([])
    setPage(0)
  }

  const handleChangeRowsPerPage = (event) => {
    setSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleCopySessionId = async (sessionId) => {
    try {
      await navigator.clipboard.writeText(sessionId)
      toast.info(`ID copiado para a área de transferência: ${sessionId}`)
      // setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err)
    }
  }


  const showResult = (log) => {
    if (!log.is_completed) {
      return <Tooltip title="Em andamento"><AccessIcon color="action" /></Tooltip>
    }
    if (log.was_successful) {
      return <Tooltip title="Sucesso"><CheckIcon color="success" /></Tooltip>
    }
    return <Tooltip title="Falha"><CancelIcon color="error" /></Tooltip>
  }

  return (
    <div className={classes.root}>
      <div className={classes.filterSection}>
        <form className={classes.filterForm} onSubmit={handleApplyFilter}>
          <FormControl className={classes.filterType} size="small">
            <Select
              value={filterType}
              onChange={(event) => {
                setFilterType(event.target.value)
                setSearchValue('')
                setStartDate(null)
                setEndDate(null)
                setSelectedActions([])
              }}
              aria-label="Tipo de filtro"
            >
              <MenuItem value="user">Usuário (email ou nome)</MenuItem>
              <MenuItem value="ip">IP</MenuItem>
              <MenuItem value="method">Método</MenuItem>
              <MenuItem value="status">Status</MenuItem>
              <MenuItem value="httpStatus">Código HTTP</MenuItem>
              <MenuItem value="actions">Ações</MenuItem>
              <MenuItem value="sessionId">ID da Sessão</MenuItem>
              <MenuItem value="dateRange">Intervalo de Datas</MenuItem>
            </Select>
          </FormControl>
          {filterType === 'dateRange' ? (
            <React.Fragment>
              <DatePicker
                label="Data Início"
                value={startDate}
                onChange={(value) => setStartDate(value)}
                maxDate={endDate || undefined}
                renderInput={(params) => (
                  <TextField {...params} className={classes.filterValue} size="small" />
                )}
              />
              <DatePicker
                label="Data Fim"
                value={endDate}
                onChange={(value) => setEndDate(value)}
                minDate={startDate || undefined}
                renderInput={(params) => (
                  <TextField {...params} className={classes.filterValue} size="small" />
                )}
              />
            </React.Fragment>
          ) : filterType === 'actions' ? (
            <Autocomplete
              className={classes.filterValue}
              multiple
              options={scopeOptions}
              value={selectedActions}
              onChange={(event, value) => setSelectedActions(value)}
              getOptionLabel={(option) => option.description || option.name || ''}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              groupBy={(option) => option.group || 'Outros'}
              filterSelectedOptions
              renderInput={(params) => <TextField {...params} size="small" label="Ações" placeholder="Selecione ações" />}
            />
          ) : filterType === 'sessionId' ? (
            <React.Fragment>
              <FormControl className={classes.sessionIdOperator} size="small">
                <Select
                  value={sessionIdOperator}
                  onChange={(event) => setSessionIdOperator(event.target.value)}
                  aria-label="Operador do ID da Sessão"
                >
                  <MenuItem value="contains">contém</MenuItem>
                  <MenuItem value="notContains">não contém</MenuItem>
                </Select>
              </FormControl>
              <TextField
                className={classes.filterValue}
                size="small"
                label="ID da Sessão"
                placeholder="Informe o ID da sessão"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
            </React.Fragment>
          ) : filterType === 'method' || filterType === 'status' ? (
            <FormControl className={classes.filterValue} size="small">
              <Select
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                displayEmpty
                aria-label={filterType === 'method' ? 'Método HTTP' : 'Status'}
                renderValue={(value) => {
                  if (!value) return filterType === 'method' ? 'Selecione um método' : 'Selecione um status'
                  if (filterType === 'status') return value === 'success' ? 'Sucesso' : 'Erro'
                  return value
                }}
              >
                {filterType === 'method' ? [
                  <MenuItem key="POST" value="POST">POST</MenuItem>,
                  <MenuItem key="GET" value="GET">GET</MenuItem>,
                  <MenuItem key="PUT" value="PUT">PUT</MenuItem>,
                  <MenuItem key="PATCH" value="PATCH">PATCH</MenuItem>,
                ] : [
                  <MenuItem key="success" value="success">Sucesso</MenuItem>,
                  <MenuItem key="error" value="error">Erro</MenuItem>,
                ]}
              </Select>
            </FormControl>
          ) : (
            <TextField
              className={classes.filterValue}
              size="small"
              label={filterType === 'user' ? 'Usuário (nome ou email)' : filterType === 'ip' ? 'Endereço IP' : 'Código HTTP'}
              placeholder={filterType === 'user' ? 'Usuário (nome ou email)' : filterType === 'ip' ? 'Endereço IP' : 'Ex.: 200'}
              type={filterType === 'httpStatus' ? 'number' : 'text'}
              inputProps={filterType === 'httpStatus' ? { min: 100, max: 599, step: 1 } : undefined}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          )}
          <Button type="submit" variant="contained">Aplicar Filtro</Button>
          <Button type="button" variant="outlined" onClick={handleClearFilters}>
            Limpar Filtros
          </Button>
        </form>
        {filters.length > 0 && (
          <div className={classes.appliedFilters}>
            {filters.map((filter) => (
              <Chip
                key={filter.type === 'dateRange' ? 'dateRange' : `${filter.type}-${filter.value}-${filter.operator || ''}`}
                label={filter.type === 'dateRange'
                  ? `Intervalo de Datas: ${filter.startDate ? moment(filter.startDate).format('DD/MM/YYYY') : '...'} a ${filter.endDate ? moment(filter.endDate).format('DD/MM/YYYY') : '...'}`
                  : filter.type === 'sessionId'
                    ? `ID da Sessão ${filter.operator === 'notContains' ? 'não contém' : 'contém'}: ${(filter.value === currentSessionId ? 'Sessão atual' : filter.value)}`
                    : `${{
                      user: 'Usuário (nome ou email)',
                      ip: 'Endereço IP',
                      method: 'Método',
                      status: 'Status',
                      httpStatus: 'Código HTTP',
                      actions: 'Ação',
                    }[filter.type]}: ${filter.type === 'status' ? (filter.value === 'success' ? 'Sucesso' : 'Erro') : filter.type === 'actions' ? filter.label : filter.value}`}
                onDelete={() => handleRemoveFilter(filters.indexOf(filter))}
                deleteIcon={<CloseIcon />}
                variant="outlined"
              />
            ))}
          </div>
        )}
      </div>
      <div className={classes.tableContainer}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={64} color="primary" />
            <span style={{ fontSize: '1.4rem' }}>Buscando registros de atividade...</span>
          </div>
        ) : (
          <table className={classes.dataTable}>
            <thead>
              <tr>
                <th>Data/hora do acesso</th>
                <th>IP de origem</th>
                <th>Método</th>
                <th>Duração</th>
                <th>Ação</th>
                <th>Usuário</th>
                <th>Função do Usuário</th>
                <th>Origem</th>
                <th>ID da Sessão</th>
                <th>Resultado</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? logs.map((log) => (
                <tr key={log.uuid}>
                  <td>{moment(log.timestamp).format('DD/MM/YYYY HH:mm:ss')}</td>
                  <td>{log.ip_address || '-'}</td>
                  <td>{log.http_method || '-'}</td>
                  <td>{formatDuration(log.request_duration_ms) || '-'}</td>
                  <td>{log.action_description || '-'}</td>
                  <td>{log.user?.name || '-'}</td>
                  <td>{log.user?.user_role || '-'}</td>
                  <td>{log.requested_from || '-'}</td>
                  <td>
                    <span style={{ display: 'flex', justifyContent: 'center',  alignItems: 'center' }}>
                      <span>
                        {(log.user_session_id || '-').substring(0, 7).toUpperCase()}
                        <br />
                        {log.user_session_id === currentSessionId && <span style={{ color: 'purple' }}>(sessão atual)</span>}
                      </span>
                      {log.user_session_id  && <IconButton onClick={() => handleCopySessionId(log.user_session_id)}>
                        <ContentCopy fontSize='small' />
                      </IconButton>}
                    </span>
                  </td>
                  <td><span>{showResult(log)} <Typography variant='body2'>{log.http_status_result}</Typography></span></td>
                  <td>
                    <IconButton
                      size="small"
                      aria-label="Ver detalhes da atividade"
                      title="Ver detalhes"
                      onClick={() => setSelectedLogUuid(log.uuid)}
                    >
                      <OpenInNew />
                    </IconButton>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="9" className={classes.emptyTable}>Nenhum registro encontrado</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        count={total}
        component="div"
        page={page}
        rowsPerPage={size}
        rowsPerPageOptions={[10, 15, 20, 25, 30]}
        onPageChange={(event, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <UserActivityLogDetails
        uuid={selectedLogUuid}
        open={Boolean(selectedLogUuid)}
        onClose={() => setSelectedLogUuid(null)}
      />
    </div>
  )
}

export default UserActivityLogMonitor
